/*
|--------------------------------------------------------------------------
| Push cart upsell carousel
|--------------------------------------------------------------------------
| Swaps in Shopify product recommendations for the newest cart items,
| followed by the hand-picked products, and drives the carousel arrows.
|
*/
if (!customElements.get('push-cart-upsell')) {
  const cache = new Map()
  // Always ask for the maximum so there are enough left after filtering (in cart / on sale)
  const FETCH_LIMIT = 10

  customElements.define(
    'push-cart-upsell',
    class PushCartUpsell extends HTMLElement {
      connectedCallback () {
        this.track = this.querySelector('[data-upsell-track]')
        this.prev = this.querySelector('[data-upsell-prev]')
        this.next = this.querySelector('[data-upsell-next]')
        if (!this.track) return

        this.updateArrows = this.updateArrows.bind(this)
        this.onPrev = () => this.step(-1)
        this.onNext = () => this.step(1)

        this.track.addEventListener('scroll', this.updateArrows, { passive: true })
        window.addEventListener('resize', this.updateArrows)
        this.prev?.addEventListener('click', this.onPrev)
        this.next?.addEventListener('click', this.onNext)

        this.connected = true
        this.updateArrows()
        if (!this.hasAttribute('data-static')) this.loadRecommendations()
      }

      disconnectedCallback () {
        this.connected = false
        this.track?.removeEventListener('scroll', this.updateArrows)
        window.removeEventListener('resize', this.updateArrows)
      }

      step (direction) {
        const card = this.track.querySelector('.psh-UpsellCard')
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0
        const distance = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth

        this.track.scrollBy({ left: direction * distance, behavior: 'smooth' })
      }

      updateArrows () {
        if (!this.track) return
        const max = this.track.scrollWidth - this.track.clientWidth - 2

        this.toggleAttribute('data-scrollable', max > 0)
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2
        if (this.next) this.next.disabled = this.track.scrollLeft >= max
      }

      list (value) {
        return (value || '').split(',').map(v => v.trim()).filter(Boolean)
      }

      async fetchCards (productId, intent) {
        const key = `${productId}|${intent}`

        if (!cache.has(key)) {
          const url = new URL(this.dataset.url || '/recommendations/products', window.location.origin)
          url.searchParams.set('section_id', 'push-cart-upsell-recs')
          url.searchParams.set('product_id', productId)
          url.searchParams.set('limit', FETCH_LIMIT)
          url.searchParams.set('intent', intent)

          cache.set(
            key,
            fetch(url)
              .then(response => (response.ok ? response.text() : ''))
              .catch(() => ''),
          )
        }

        const html = await cache.get(key)
        if (!html) return []

        const doc = new DOMParser().parseFromString(html, 'text/html')
        let cards = [...doc.querySelectorAll('.psh-UpsellCard')]
        if (this.hasAttribute('data-exclude-sale')) {
          cards = cards.filter(card => !card.hasAttribute('data-on-sale'))
        }
        return cards
      }

      async loadRecommendations () {
        const seeds = this.list(this.dataset.productIds).slice(0, 2)
        if (!seeds.length) return

        const exclude = new Set(this.list(this.dataset.exclude))
        const limit = parseInt(this.dataset.limit, 10) || 8
        const intent = this.dataset.intent || 'related'

        try {
          let lists = await Promise.all(seeds.map(id => this.fetchCards(id, intent)))

          if (intent !== 'related' && lists.every(l => !l.length)) {
            lists = await Promise.all(seeds.map(id => this.fetchCards(id, 'related')))
          }

          if (!this.connected) return

          const seen = new Set(exclude)
          const cards = []
          const add = card => {
            const id = card.dataset.productId
            if (!id || seen.has(id)) return
            seen.add(id)
            cards.push(card)
          }

          // Interleave recommendations from each seed product, then hand-picked fallbacks
          const longest = Math.max(0, ...lists.map(l => l.length))
          for (let i = 0; i < longest; i++) lists.forEach(l => l[i] && add(l[i]))
          this.track.querySelectorAll('.psh-UpsellCard').forEach(add)

          if (!cards.length) return

          this.track.replaceChildren(...cards.slice(0, limit).map(card => document.adoptNode(card)))
          this.track.scrollLeft = 0
          this.hidden = false
          this.updateArrows()
        } catch (error) {
          console.warn('[push-cart-upsell]', error)
        }
      }
    },
  )
}
